import { useCallback, useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import { CONDITION_STYLES } from './condition-utils'
import { EQUIPMENT_TABLE_GRID } from './equipment-table-columns'
import {
  usePropertyDefinitionsByCodes,
  useSetAssetPropertyValue,
  useUpdateAsset,
  useUpdateAssetPropertyValue,
} from '../../hooks/useQueries'
import { refreshAssetEquipmentLists } from '../../hooks/useAssetEquipment'
import { AppIcon } from '../ui/icon'
import { ServesLine } from '../asset-relations/NetworksSection'
import { mdiChevronDown, mdiChevronRight, mdiLinkVariant, mdiPlus, mdiShieldCheckOutline, mdiWrench } from '@mdi/js'
import { useEquipmentVia } from './EquipmentViaContext'
import { InspectionsCell } from './InspectionsCell'
import { useMeterSupplyEntry } from '../meters/MeterSupplyContext'
import { MeterSupplyStatusChip } from '../meters/MeterSupplyStatusChip'
import { formatAgeOverLifespan } from './lifespan'
import type {
  EquipmentContractRef,
  EquipmentRow as EquipmentRowData,
} from '../../api/equipment-tab-types'

interface EquipmentRowProps {
  org: string
  row: EquipmentRowData
  /** Nesting level of a component under its equipment (spec 2026-10-27-17). */
  depth?: number
  /** The « N composants » toggle of an equipment that has components. */
  componentsToggle?: { count: number; expanded: boolean; onToggle: () => void }
  /** Opens "Add a component" with this row as the assembly. */
  onAddComponent?: (assemblyUid: string) => void
}

/**
 * Storable `equipment_condition` enum values, in ontology order (1..5). The
 * canonical definition lives in `res-provisioning/types/01-properties.json`;
 * `unassessed` is deliberately absent — it is the *absence* of a value, not a
 * value, so it is offered as the empty option of the editor instead.
 */
const CONDITION_ENUM_VALUES = [
  { value: '1', condition: 'new' },
  { value: '2', condition: 'good' },
  { value: '3', condition: 'fair' },
  { value: '4', condition: 'poor' },
  { value: '5', condition: 'end_of_life' },
] as const

const CONDITION_CODE = 'equipment_condition'

/**
 * Single row of the trade equipment table. Column order and widths are shared
 * with the header via EQUIPMENT_TABLE_GRID so the two never drift.
 *
 * État and Mise en service are click-to-edit (pattern §3): Enter / blur saves,
 * Escape cancels, the write refreshes through the cache helpers. État is a
 * property value; Mise en service is the asset's own `start_date` column — the
 * single source of truth for an equipment's commissioning date since spec
 * 2026-09-03-02 — so it PATCHes the asset instead. Maintenance and Garantie are links
 * to the contract actually linked to this equipment through `contract_assets`
 * — no contract, no link. Vérifications summarises the periodic inspections
 * of the equipment and opens their list with the reports.
 */
export function EquipmentRow({ org, row, depth = 0, componentsToggle, onAddComponent }: EquipmentRowProps) {
  const { t } = useTranslation('assets')
  const viaAssembly = useEquipmentVia(row.id)
  // Metering rows only: the supply status of the meter (spec 2026-10-27-16 §2).
  const meterSupply = useMeterSupplyEntry(row.id)
  const queryClient = useQueryClient()

  const [editingCondition, setEditingCondition] = useState(false)
  const [editingCommissioned, setEditingCommissioned] = useState(false)
  const [commissionedDraft, setCommissionedDraft] = useState('')
  const [editingQuantity, setEditingQuantity] = useState(false)
  const [quantityDraft, setQuantityDraft] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)

  // Property definition UIDs are needed to *create* a value (the bulk POST is
  // keyed by uid). Updating an existing one accepts the code, but we resolve
  // both from the same cached query — TanStack Query dedupes it across rows.
  const { data: propertyDefinitions = [] } = usePropertyDefinitionsByCodes(org, [CONDITION_CODE])
  const definitionUid = (code: string) =>
    propertyDefinitions.find((p) => p.code === code)?.uid

  const setPropertyValue = useSetAssetPropertyValue(org, row.id)
  const updatePropertyValue = useUpdateAssetPropertyValue(org, row.id)
  const updateAsset = useUpdateAsset(org, row.id)

  const condStyle = CONDITION_STYLES[row.condition] || CONDITION_STYLES.unassessed
  const lifespan = Number(row.lifespan_years) || 0
  const age = Number(row.age_years) || 0
  // Cap the bar fill at 100%.
  const ratio = lifespan > 0 ? Math.min(1, age / lifespan) : 0
  const barWidthPct = Math.round(ratio * 100)
  const ageOverLifespan = lifespan > 0 && age >= 0 ? formatAgeOverLifespan(t, age, lifespan) : null

  useEffect(() => {
    setCommissionedDraft(row.start_date ?? '')
  }, [row.start_date])

  useEffect(() => {
    setQuantityDraft(String(row.quantity ?? 1))
  }, [row.quantity])

  /**
   * Write a property value: PATCH when the asset already carries one (the
   * PATCH endpoint 404s on a missing value), bulk-POST otherwise.
   */
  const savePropertyValue = useCallback(
    async (code: string, value: string | number | null, exists: boolean) => {
      setSaveError(null)
      const uid = definitionUid(code)
      if (!uid) {
        setSaveError(t('equipment_tab.row.save_failed'))
        return false
      }
      try {
        if (exists) {
          await updatePropertyValue.mutateAsync(uid, { value })
        } else {
          await setPropertyValue.mutateAsync({ properties: [{ property_uid: uid, value }] })
        }
        refreshAssetEquipmentLists(queryClient, org)
        return true
      } catch (error) {
        const message =
          (error as { title?: string; message?: string })?.title ||
          (error as { message?: string })?.message ||
          t('equipment_tab.row.save_failed')
        setSaveError(message)
        return false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [propertyDefinitions, updatePropertyValue, setPropertyValue, queryClient, org, t],
  )

  const handleConditionChange = useCallback(
    async (next: string) => {
      setEditingCondition(false)
      if (!next) return
      await savePropertyValue(CONDITION_CODE, next, !!row.has_condition_value)
    },
    [savePropertyValue, row.has_condition_value],
  )

  /**
   * Commissioning date — a write to `assets.start_date`, not to a property.
   * An empty draft clears the column (a legitimate "we don't know" state).
   */
  const commitCommissioned = useCallback(async () => {
    const trimmed = commissionedDraft.trim()
    const current = row.start_date ?? ''
    if (trimmed === current) {
      setEditingCommissioned(false)
      return
    }
    if (trimmed !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      setSaveError(t('equipment_tab.row.commissioned_invalid'))
      return
    }
    setSaveError(null)
    try {
      await updateAsset.mutateAsync({ start_date: trimmed || null } as never)
      refreshAssetEquipmentLists(queryClient, org)
      setEditingCommissioned(false)
    } catch (error) {
      const message =
        (error as { title?: string; message?: string })?.title ||
        (error as { message?: string })?.message ||
        t('equipment_tab.row.save_failed')
      setSaveError(message)
    }
  }, [commissionedDraft, row.start_date, updateAsset, queryClient, org, t])

  const cancelCommissioned = useCallback(() => {
    setCommissionedDraft(row.start_date ?? '')
    setEditingCommissioned(false)
    setSaveError(null)
  }, [row.start_date])

  /**
   * Nombre — a write to `assets.quantity` (spec 2026-09-22-05 / issue #1375),
   * PATCHed on the asset like `commitCommissioned`. Offered only when the type allows
   * several units per asset (`row.multiple`); the API refuses `quantity > 1`
   * on every other type, so the cell is read-only there and says why.
   */
  const commitQuantity = useCallback(async () => {
    const trimmed = quantityDraft.trim()
    const current = row.quantity ?? 1
    const parsed = Number(trimmed)
    if (trimmed === '' || !Number.isInteger(parsed) || parsed < 1) {
      setSaveError(t('equipment_tab.row.quantity_invalid'))
      return
    }
    if (parsed === current) {
      setEditingQuantity(false)
      return
    }
    setSaveError(null)
    try {
      await updateAsset.mutateAsync({ quantity: parsed } as never)
      refreshAssetEquipmentLists(queryClient, org)
      setEditingQuantity(false)
    } catch (error) {
      const message =
        (error as { title?: string; message?: string })?.title ||
        (error as { message?: string })?.message ||
        t('equipment_tab.row.save_failed')
      setSaveError(message)
    }
  }, [quantityDraft, row.quantity, updateAsset, queryClient, org, t])

  const cancelQuantity = useCallback(() => {
    setQuantityDraft(String(row.quantity ?? 1))
    setEditingQuantity(false)
    setSaveError(null)
  }, [row.quantity])

  return (
    <div className="group border-t border-border/60 text-sm">
      <div
        className={cn(EQUIPMENT_TABLE_GRID, 'items-center py-2 px-4')}
        role="row"
        data-testid={`equipment-tab-row-${row.id}`}
      >
        {/* Équipement — name + sub-line (type · rattachement). A component
            is indented under its equipment (spec 2026-10-27-17). */}
        <div
          className={cn('min-w-0', depth > 0 && 'border-l-2 border-border/70')}
          style={depth > 0 ? { paddingLeft: `${depth * 0.75}rem`, marginLeft: `${(depth - 1) * 0.75}rem` } : undefined}
          role="cell"
          data-testid={depth > 0 ? `equipment-tab-row-${row.id}-nested` : undefined}
        >
          <Link
            to={`/$org/assets/$assetUid`}
            params={{ org, assetUid: row.id }}
            className="block text-foreground font-medium truncate hover:underline"
            data-testid={`equipment-tab-row-${row.id}-link`}
          >
            {row.name}
          </Link>
          <div className="text-xs text-muted-foreground truncate mt-0.5">
            {[row.type_label, row.location_label].filter(Boolean).join(' · ') || '—'}
          </div>
          {(componentsToggle || onAddComponent) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {componentsToggle && (
                <button
                  type="button"
                  onClick={componentsToggle.onToggle}
                  aria-expanded={componentsToggle.expanded}
                  className="inline-flex items-center gap-0.5 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                  data-testid={`equipment-row-components-toggle-${row.id}`}
                >
                  <AppIcon path={componentsToggle.expanded ? mdiChevronDown : mdiChevronRight} size="xs" aria-hidden />
                  {t('components.count', { count: componentsToggle.count })}
                </button>
              )}
              {onAddComponent && (
                <button
                  type="button"
                  onClick={() => onAddComponent(row.id)}
                  className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs text-primary hover:underline md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
                  title={t('components.add')}
                  data-testid={`equipment-row-add-component-${row.id}`}
                >
                  <AppIcon path={mdiPlus} size="xs" aria-hidden />
                  {t('components.add_short')}
                </button>
              )}
            </div>
          )}
          {meterSupply && (
            <div className="mt-0.5">
              <MeterSupplyStatusChip entry={meterSupply} />
            </div>
          )}
          {/* What this equipment feeds (spec 2026-10-25-24). */}
          <ServesLine org={org} equipmentUid={row.id} />
          {/* This row's physical tree parent is outside the subtree — it is
              listed here because it is part of an assembly that lives here
              (spec 2026-08-16-03 §6). Its own line so the assembly name is
              never eaten by the name cell's truncation. */}
          {viaAssembly && (
            <div
              className="mt-0.5 inline-flex max-w-full items-center gap-0.5 truncate text-xs text-muted-foreground"
              title={t('equipment_tab.via_assembly', {
                name: viaAssembly,
                defaultValue: 'Listed here via {{name}}',
              })}
              data-testid={`equipment-tab-row-${row.id}-via`}
            >
              <AppIcon path={mdiLinkVariant} size="xs" className="shrink-0" aria-hidden />
              <span className="truncate">
                {t('equipment_tab.via_assembly_short', {
                  name: viaAssembly,
                  defaultValue: 'via {{name}}',
                })}
              </span>
            </div>
          )}
        </div>

        {/* État — click the pill to edit. */}
        <div className="min-w-0" role="cell">
          {editingCondition ? (
            <select
              autoFocus
              className="w-full rounded-md border border-input bg-background px-1.5 py-1 text-xs text-foreground"
              defaultValue={
                CONDITION_ENUM_VALUES.find((c) => c.condition === row.condition)?.value ?? ''
              }
              onChange={(e) => void handleConditionChange(e.target.value)}
              onBlur={() => setEditingCondition(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditingCondition(false)
                }
              }}
              aria-label={t('equipment_tab.columns.condition')}
              data-testid={`equipment-tab-row-${row.id}-condition-select`}
            >
              <option value="">{t('equipment_tab.condition.unassessed')}</option>
              {CONDITION_ENUM_VALUES.map((c) => (
                <option key={c.value} value={c.value}>
                  {t(`equipment_tab.condition.${c.condition}`)}
                </option>
              ))}
            </select>
          ) : (
            <button
              type="button"
              onClick={() => setEditingCondition(true)}
              className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('equipment_tab.row.edit_condition')}
              title={t('equipment_tab.row.edit_condition')}
              data-testid={`equipment-tab-row-${row.id}-condition`}
            >
              <Badge className={cn(condStyle.pill, 'cursor-pointer')}>
                {t(`equipment_tab.condition.${row.condition}`)}
              </Badge>
            </button>
          )}
        </div>

        {/* Mise en service — click the year to edit; age bar underneath. */}
        <div className="min-w-0" role="cell">
          {editingCommissioned ? (
            <input
              autoFocus
              type="date"
              value={commissionedDraft}
              onChange={(e) => setCommissionedDraft(e.target.value)}
              onBlur={() => void commitCommissioned()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void commitCommissioned()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelCommissioned()
                }
              }}
              className="w-36 rounded-md border border-input bg-background px-1.5 py-1 text-xs text-foreground"
              aria-label={t('equipment_tab.columns.commissioned')}
              data-testid={`equipment-tab-row-${row.id}-commissioned-input`}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditingCommissioned(true)}
              className="block text-left text-xs text-foreground hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('equipment_tab.row.edit_commissioned')}
              title={t('equipment_tab.row.edit_commissioned')}
              data-testid={`equipment-tab-row-${row.id}-commissioned`}
            >
              {row.start_date ?? '—'}
            </button>
          )}
          {ageOverLifespan && (
            <>
              <div className="text-[10px] text-muted-foreground mt-0.5">{ageOverLifespan}</div>
              <div
                className="mt-1 w-full max-w-[120px] h-1 rounded-full bg-muted overflow-hidden"
                role="progressbar"
                aria-valuenow={barWidthPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('equipment_tab.row.age_progress')}
              >
                <div className={`h-full ${condStyle.bar}`} style={{ width: `${barWidthPct}%` }} />
              </div>
            </>
          )}
        </div>

        {/* Maintenance — link to the contract, when one is linked. */}
        <div className="flex items-center" role="cell">
          <ContractCell
            org={org}
            contract={row.maintenance_contract ?? null}
            icon={<AppIcon path={mdiWrench} size="xs" />}
            label={t('equipment_tab.columns.maintenance')}
            noneLabel={t('equipment_tab.row.no_maintenance_contract')}
            testid={`equipment-tab-row-${row.id}-maintenance`}
          />
          {row.maintenance_via && (
            <span
              className="ml-1 truncate text-[10px] text-muted-foreground"
              title={t('components.maintenance_via', { name: row.maintenance_via })}
              data-testid={`equipment-tab-row-${row.id}-maintenance-via`}
            >
              {t('components.maintenance_via', { name: row.maintenance_via })}
            </span>
          )}
        </div>

        {/* Garantie — link to the works contract backing it, when one is linked. */}
        <div className="flex items-center" role="cell">
          <ContractCell
            org={org}
            contract={row.warranty_contract ?? null}
            icon={<AppIcon path={mdiShieldCheckOutline} size="xs" />}
            label={t('equipment_tab.columns.warranty')}
            noneLabel={t('equipment_tab.row.no_warranty_contract')}
            testid={`equipment-tab-row-${row.id}-warranty`}
          />
        </div>

        {/* Vérifications — periodic inspections of this equipment (VGP, dry riser,
            lifeline...), never the regulatory obligations of the building. */}
        <div className="flex min-w-0 items-center" role="cell">
          <InspectionsCell
            org={org}
            items={row.inspections ?? []}
            testid={`equipment-tab-row-${row.id}-inspections`}
          />
        </div>

        {/* Nombre — click to edit when the type allows several units per asset;
            a direct asset attribute (spec 2026-09-22-05), so it PATCHes the asset. */}
        <div className="text-right text-xs tabular-nums" role="cell">
          {row.multiple && editingQuantity ? (
            <input
              autoFocus
              type="number"
              min={1}
              step={1}
              value={quantityDraft}
              onChange={(e) => setQuantityDraft(e.target.value)}
              onBlur={() => void commitQuantity()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void commitQuantity()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelQuantity()
                }
              }}
              className="w-16 rounded-md border border-input bg-background px-1.5 py-1 text-right text-xs text-foreground"
              aria-label={t('equipment_tab.columns.quantity')}
              data-testid={`equipment-tab-row-${row.id}-quantity-input`}
            />
          ) : row.multiple ? (
            <button
              type="button"
              onClick={() => setEditingQuantity(true)}
              className="text-foreground hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('equipment_tab.row.edit_quantity')}
              title={t('equipment_tab.row.edit_quantity')}
              data-testid={`equipment-tab-row-${row.id}-quantity`}
            >
              {row.quantity ?? 1}
            </button>
          ) : (
            <span
              className="text-muted-foreground cursor-default"
              title={t('equipment_tab.row.quantity_unit_tracked')}
              data-testid={`equipment-tab-row-${row.id}-quantity`}
              data-editable="false"
            >
              {row.quantity ?? 1}
            </span>
          )}
        </div>
      </div>

      {saveError && (
        <div
          className="px-4 pb-2 text-xs text-destructive"
          data-testid={`equipment-tab-row-${row.id}-error`}
        >
          {saveError}
        </div>
      )}
    </div>
  )
}

/**
 * Maintenance / Garantie cell. When a contract is linked it renders a
 * success-tinted icon that navigates to the contract; when none is linked the
 * icon stays muted and inert. The meaning of the icon comes from the column
 * header — the tooltip names the contract.
 */
function ContractCell({
  org,
  contract,
  icon,
  label,
  noneLabel,
  testid,
}: {
  org: string
  contract: EquipmentContractRef | null
  icon: React.ReactNode
  label: string
  noneLabel: string
  testid: string
}) {
  if (!contract) {
    return (
      <span
        className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground/50"
        title={noneLabel}
        aria-label={noneLabel}
        data-testid={testid}
        data-active="false"
      >
        {icon}
      </span>
    )
  }

  const title = `${label} : ${contract.name}`
  return (
    <Link
      to="/$org/contracts/$contractUid"
      params={{ org, contractUid: contract.uid }}
      className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-success/25 bg-success/10 text-success hover:bg-success/20"
      title={title}
      aria-label={title}
      data-testid={testid}
      data-active="true"
    >
      {icon}
    </Link>
  )
}
